import { useEffect, useMemo, useState } from 'react'
import s from './ExternalArticlesReport.modules.scss'
import { useNotification, usePermissions } from 'hooks'
import { externalArticlesReportsRoles } from '../constants'
import { DeleteConfirmationModal } from 'components/core-ui/DeleteConfirmationModal'
import { Typography } from '@mui/material'
import { Table } from 'components/core-ui/Table'
import { ExternalArticlesReportModal } from './ExternalArticlesReportModal'
import {
  AddActionIcon,
  DeleteActionIcon,
  DownloadActionIcon,
  EditActionIcon,
  PrintActionIcon,
  UploadActionIcon
} from 'icons'
import { getColumns, getFilters } from './helpers'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { formatErrorMessage } from 'utils/formatting'
import { toTableRowItems } from './serializers'
import { downloadDocument, getWasteCategoryOptions } from '../api'
import { UploadFilesModal } from 'components/core-ui/UploadFilesModal'
import { handleQueryError } from 'utils/helpers'
import useUpload from '../common/hooks/useUpload'
import { PrintPreviewModal } from '../common/PrintPreviewModal'

const ExternalArticlesReport = () => {
  const [tableItems, setTableItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [isUploadFilesModalOpen, setIsUploadFilesModalOpen] = useState<boolean>(false)
  const [isPrintPreviewModalOpen, setIsPrintPreviewModalOpen] = useState<boolean>(false)
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const [isPdfBlobLoading, setIsPdfBlobLoading] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<any>()
  const [shouldRefetchItem, setShouldRefetchItem] = useState(false)
  const [rowDownloadId, setRowDownloadId] = useState<number>()

  const { showNotification } = useNotification()

  const { canCreate, canEdit, canDelete } = usePermissions(externalArticlesReportsRoles)

  const openNewModal = (): void => setIsNewModalOpen(true)
  const closeNewModal = (): void => setIsNewModalOpen(false)

  const openEditModal = (): void => {
    setIsEditModalOpen(true)
    setShouldRefetchItem(true)
  }
  const closeEditModal = (): void => {
    setIsEditModalOpen(false)
    setShouldRefetchItem(false)
  }

  const openUploadFilesModal = (): void => setIsUploadFilesModalOpen(true)
  const closeUploadFilesModal = (): void => setIsUploadFilesModalOpen(false)

  const openDeleteModal = (): void => setIsDeleteModalOpen(true)
  const closeDeleteModal = (): void => setIsDeleteModalOpen(false)

  const openPrintPreviewModal = (): void => setIsPrintPreviewModalOpen(true)
  const closePrintPreviewModal = (): void => {
    setIsPrintPreviewModalOpen(false)
    setPdfBlob(null)
  }

  const { data: wasteCategoryOptions } = getWasteCategoryOptions(showNotification)

  const downloadQuery = downloadDocument(
    showNotification,
    'EXTERNAL_ARTICLE',
    rowDownloadId,
    'EXTERNAL_PV',
    () => setRowDownloadId(0)
  )
  useEffect(() => {
    if (rowDownloadId) {
      downloadQuery.refetch()
    }
  }, [rowDownloadId])

  const toolbarActions = useMemo(() => {
    if (canCreate) {
      return [
        {
          label: 'Adauga',
          icon: <AddActionIcon />,
          onClick: () => openNewModal()
        }
      ]
    }

    return []
  }, [canCreate])

  const columns = useMemo(() => getColumns(), [])

  const filters = useMemo(() => getFilters(wasteCategoryOptions), [wasteCategoryOptions])

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/waste-management-system/external-articles',
          filters: [{ name: 'currentLocation', value: true }],
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor pentru rapoartele produselor externe. ${formatErrorMessage(
          error
        )}`,
        type: 'error'
      })
    }
  }

  const selectRow = (rowId: number, action: string) => {
    const item = tableItems.find(item => item.id === rowId)
    setSelectedRow(item)

    switch (action) {
      case 'edit':
        openEditModal()
        break
      case 'upload':
        openUploadFilesModal()
        break
      case 'delete':
        openDeleteModal()
        break
      default:
        break
    }
  }

  const { saveDocuments } = useUpload({
    reportId: selectedRow?.id,
    reportType: 'EXTERNAL_ARTICLE',
    onSuccess: () => {
      setShouldRefreshData(true)
      closeUploadFilesModal()
      showNotification({
        message: `Fisierele au fost incarcate cu success.`,
        type: 'success'
      })
    },
    onError: error => {
      handleQueryError(error, `A aparut o eroare la incarcarea fisierelor.`, showNotification)
    }
  })

  const printPDF = async (rowId: number) => {
    try {
      setIsPdfBlobLoading(true)
      openPrintPreviewModal()

      const response = await makeCoreGetRequest(
        `/waste-management-system/external-articles/${rowId}/download`,
        {
          responseType: 'blob'
        }
      )

      const blob = new Blob([response], { type: 'application/pdf' })
      setPdfBlob(blob)
    } catch (error) {
      closePrintPreviewModal()
      showNotification({
        message: `A aparut o eroare la generarea raportului PDF. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    } finally {
      setIsPdfBlobLoading(false)
    }
  }

  return (
    <div className={s.mainContainer}>
      {canCreate && (
        <ExternalArticlesReportModal
          isModalOpen={isNewModalOpen}
          closeModal={closeNewModal}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      {canEdit && (
        <ExternalArticlesReportModal
          isModalOpen={isEditModalOpen}
          closeModal={closeEditModal}
          editItemId={selectedRow?.id}
          shouldRefetchItem={shouldRefetchItem}
          setShouldRefreshData={setShouldRefreshData}
          isEdit
        />
      )}
      {canEdit && (
        <UploadFilesModal
          isModalOpen={isUploadFilesModalOpen}
          closeModal={closeUploadFilesModal}
          saveDocuments={saveDocuments}
          documentType={{ value: 'EXTERNAL_PV', label: 'Raport Articole Externe' }}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        />
      )}
      {canDelete && (
        <DeleteConfirmationModal
          isModalOpen={isDeleteModalOpen}
          closeModal={closeDeleteModal}
          actionUrl="/waste-management-system/external-articles/:id"
          itemId={`${selectedRow?.id}`}
          content={<Typography>Sunteti sigur ca vreti sa stergeti acest raport?</Typography>}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      <PrintPreviewModal
        isModalOpen={isPrintPreviewModalOpen}
        closeModal={closePrintPreviewModal}
        pdfBlob={pdfBlob}
        isPdfBlobLoading={isPdfBlobLoading}
      />
      <Table
        hideCheckbox
        orderBy="creationDate"
        order="desc"
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        shouldResetPage={shouldResetPage}
        setShouldResetPage={setShouldResetPage}
        rowActions={[
          {
            label: 'Print',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#4A148C',
            desktopIcon: <PrintActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => printPDF(rowId),
            showTooltip: true
          },
          canEdit && {
            label: 'Upload',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#FFB300',
            desktopIcon: <UploadActionIcon />,
            onClick: (_e, rowId) => selectRow(rowId, 'upload'),
            showTooltip: true
          },
          {
            label: 'Download',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#4A148C',
            desktopIcon: <DownloadActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => setRowDownloadId(rowId),
            showTooltip: true
          },
          canEdit && {
            label: 'Editeaza',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#16a085',
            desktopIcon: <EditActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => selectRow(rowId, 'edit'),
            hideCondition: { field: 'isHandedOver', value: true },
            showTooltip: true
          },
          canDelete && {
            label: 'Sterge',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#d9534f',
            desktopIcon: <DeleteActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => selectRow(rowId, 'delete'),
            hideCondition: { field: 'isHandedOver', value: true },
            showTooltip: true
          }
        ]}
        rowActionsWidth="300px"
        toolbarActions={toolbarActions}
        filters={filters}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
      />
    </div>
  )
}

export default ExternalArticlesReport
